/**
 * Panel 1: Force Composition — Readiness vs Threat scatter + sortable table
 * Maps to ARK's Portfolio Map panel
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import type { Domain, AssetSummary } from '../../../types'
import { DOMAIN_CONFIG, TEMPO_CONFIG, type Tempo } from '../../../types'
import type { FuelState, AssetLoadout } from '../../../types'
import { getForceComposition, getTheaterFuel, getTheaterOrdnance, formatPercent, formatScore } from '../../../services/api'
import { AssetSilhouette } from '../../shared/AssetSilhouette'
import { AssetCardGrid } from './AssetCardGrid'

function domainColor(domain: string): string {
  return DOMAIN_CONFIG[domain as Domain]?.color ?? '#6366f1'
}

function tempoClass(tempo: string): string {
  return TEMPO_CONFIG[tempo as Tempo]?.cssClass ?? 'tempo-steady'
}

type SortKey = 'asset_type' | 'domain' | 'mc_rate' | 'readiness_score' | 'threat_exposure' | 'current_tempo' | 'assigned_count'

interface ForceCompositionPanelProps {
  activeDomainFilter: Domain | null
  onSelectAsset: (assetId: string) => void
}

export function ForceCompositionPanel({ activeDomainFilter, onSelectAsset }: ForceCompositionPanelProps) {
  const [assets, setAssets] = useState<AssetSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<SortKey>('mc_rate')
  const [sortAsc, setSortAsc] = useState(true)
  const [hoveredAsset, setHoveredAsset] = useState<string | null>(null)
  const [fleetMC, setFleetMC] = useState(0)
  const [fuelMap, setFuelMap] = useState<Record<string, FuelState>>({})
  const [loadoutMap, setLoadoutMap] = useState<Record<string, AssetLoadout>>({})
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [forceRes, fuelRes, ordRes] = await Promise.all([
        getForceComposition(activeDomainFilter ?? undefined),
        getTheaterFuel(),
        getTheaterOrdnance(),
      ])
      if (forceRes.error) throw new Error(forceRes.error)
      if (forceRes.data) {
        setAssets(forceRes.data.assets)
        setFleetMC(forceRes.data.fleet_wide_mc_rate)
      }
      if (fuelRes.data) {
        const fm: Record<string, FuelState> = {}
        for (const s of fuelRes.data.fuel_states) fm[s.asset_id] = s
        setFuelMap(fm)
      }
      if (ordRes.data) {
        const lm: Record<string, AssetLoadout> = {}
        for (const lo of ordRes.data.loadouts) lm[lo.asset_id] = lo
        setLoadoutMap(lm)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch force composition')
    } finally {
      setLoading(false)
    }
  }, [activeDomainFilter])

  useEffect(() => { fetchData() }, [fetchData])

  const sorted = useMemo(() => {
    const arr = [...assets]
    arr.sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'asset_type': cmp = a.asset_type.localeCompare(b.asset_type); break
        case 'domain': cmp = a.domain.localeCompare(b.domain); break
        case 'mc_rate': cmp = a.mc_rate - b.mc_rate; break
        case 'readiness_score': cmp = a.readiness_score - b.readiness_score; break
        case 'threat_exposure': cmp = a.threat_exposure - b.threat_exposure; break
        case 'current_tempo': cmp = a.current_tempo.localeCompare(b.current_tempo); break
        case 'assigned_count': cmp = a.assigned_count - b.assigned_count; break
      }
      return sortAsc ? cmp : -cmp
    })
    return arr
  }, [assets, sortKey, sortAsc])

  const stats = useMemo(() => {
    const total = assets.length
    const redAssets = assets.filter(a => a.mc_rate < 0.5).length
    const avgReadiness = total > 0 ? assets.reduce((s, a) => s + a.readiness_score, 0) / total : 0
    return { total, redAssets, avgReadiness }
  }, [assets])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc)
    else { setSortKey(key); setSortAsc(false) }
  }

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? ' \u25B2' : ' \u25BC') : ''

  if (loading) {
    return (
      <div className="flex flex-col gap-4 animate-pulse">
        <div className="flex gap-4">{[1,2,3,4].map(i => <div key={i} className="flex-1 h-16 bg-term-bgLight rounded border border-term-border/30" />)}</div>
        <div className="h-80 bg-term-bgLight rounded border border-term-border/30" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-term-error text-lg font-semibold">Error loading force composition</div>
        <div className="text-term-textDim text-sm">{error}</div>
        <button onClick={fetchData} className="term-button term-button-primary">Retry</button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Stats Bar */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Asset Types" value={String(stats.total)} icon="@" />
        <StatCard label="Fleet MC Rate" value={formatPercent(fleetMC)} icon="%" highlight={fleetMC < 0.65} />
        <StatCard label="Below 50% MC" value={String(stats.redAssets)} icon="!" highlight={stats.redAssets > 0} />
        <StatCard label="Avg Readiness" value={formatScore(stats.avgReadiness)} icon="*" />
      </div>

      {/* Scatter Plot: Readiness vs Threat */}
      <div className="term-box p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
            Readiness Score vs Threat Exposure
          </div>
          <div className="flex gap-3 text-xs">
            {(Object.keys(DOMAIN_CONFIG) as Domain[]).map(d => (
              <span key={d} className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: domainColor(d) }} />
                <span className="text-term-textDim">{DOMAIN_CONFIG[d].label}</span>
              </span>
            ))}
          </div>
        </div>

        <div className="relative border border-term-border rounded bg-term-bgDark" style={{ height: 280 }}>
          {/* Y-axis label */}
          <div className="absolute text-[10px] text-term-textDim" style={{ left: -2, top: '50%', transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>
            Readiness Score
          </div>

          {/* X-axis label */}
          <div className="absolute bottom-[-18px] left-1/2 -translate-x-1/2 text-[10px] text-term-textDim">
            Threat Exposure
          </div>

          {/* Grid + ticks */}
          {[0, 25, 50, 75, 100].map(v => (
            <div key={`y-${v}`} className="absolute text-[9px] text-term-textDim" style={{ left: 8, top: `${100 - v}%`, transform: 'translateY(-50%)' }}>{v}</div>
          ))}
          {[0, 0.25, 0.5, 0.75, 1.0].map(v => (
            <div key={`x-${v}`} className="absolute text-[9px] text-term-textDim" style={{ left: `${v * 100}%`, bottom: 2, transform: 'translateX(-50%)' }}>{v.toFixed(2)}</div>
          ))}

          {/* Plot area */}
          <div className="absolute" style={{ left: 28, right: 8, top: 8, bottom: 16 }}>
            {assets.map(a => {
              const x = `${(a.threat_exposure) * 100}%`
              const y = `${100 - a.readiness_score}%`
              const isHovered = hoveredAsset === a.id
              return (
                <div
                  key={a.id}
                  className="absolute cursor-pointer transition-all duration-150"
                  style={{ left: x, top: y, transform: 'translate(-50%, -50%)', zIndex: isHovered ? 50 : 1 }}
                  onMouseEnter={() => setHoveredAsset(a.id)}
                  onMouseLeave={() => setHoveredAsset(null)}
                  onClick={() => onSelectAsset(a.id)}
                >
                  <div
                    className="rounded-full transition-all duration-150"
                    style={{
                      width: isHovered ? 10 : 7,
                      height: isHovered ? 10 : 7,
                      backgroundColor: domainColor(a.domain),
                      boxShadow: isHovered ? `0 0 12px ${domainColor(a.domain)}80` : `0 0 4px ${domainColor(a.domain)}40`,
                    }}
                  />
                  <div
                    className="absolute whitespace-nowrap text-[10px] font-bold transition-all duration-150"
                    style={{ left: 12, top: '50%', transform: 'translateY(-50%)', color: isHovered ? domainColor(a.domain) : '#e0e0e0', opacity: isHovered ? 1 : 0.8, textShadow: '0 0 4px rgba(0,0,0,0.8)' }}
                  >
                    {a.asset_type.split(' ')[0]}
                  </div>

                  {isHovered && (
                    <div className="absolute z-50 term-box p-2 text-[10px] leading-relaxed whitespace-nowrap" style={{ left: 14, top: -60, borderColor: domainColor(a.domain), minWidth: 200 }}>
                      <div className="font-bold text-term-text">{a.asset_type}</div>
                      <div className="text-term-textDim mt-0.5">{a.role}</div>
                      <div className="text-term-textDim mt-0.5">
                        MC: {formatPercent(a.mc_rate)} | Readiness: {a.readiness_score.toFixed(0)} | Threat: {a.threat_exposure.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Quadrant labels */}
          <div className="absolute text-[9px] text-term-textDim/40 font-medium" style={{ right: 16, top: 12 }}>HIGH READINESS / HIGH THREAT</div>
          <div className="absolute text-[9px] text-term-textDim/40 font-medium" style={{ left: 34, bottom: 20 }}>LOW READINESS / LOW THREAT</div>
        </div>
        <div className="text-term-textDim text-[10px] text-center mt-1">Click any dot to view asset readiness</div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setViewMode('cards')}
            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-colors
                       ${viewMode === 'cards'
                         ? 'bg-term-primary/20 text-term-primary border border-term-primary/40'
                         : 'text-term-textDim border border-term-border hover:text-term-text'}`}
          >
            Cards
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-colors
                       ${viewMode === 'table'
                         ? 'bg-term-primary/20 text-term-primary border border-term-primary/40'
                         : 'text-term-textDim border border-term-border hover:text-term-text'}`}
          >
            Table
          </button>
        </div>
        <span className="text-[10px] text-term-textDim">
          {assets.length} asset types{activeDomainFilter && <span className="ml-1">filtered by <span style={{ color: domainColor(activeDomainFilter) }}>{activeDomainFilter}</span></span>}
        </span>
      </div>

      {/* Cards View */}
      {viewMode === 'cards' && (
        <AssetCardGrid
          assets={sorted}
          fuelMap={fuelMap}
          loadoutMap={loadoutMap}
          onSelectAsset={onSelectAsset}
        />
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <>
          <div className="term-box overflow-auto flex-1 min-h-0">
            <table className="term-table">
              <thead className="sticky top-0 bg-term-bg z-10">
                <tr>
                  <th className="cursor-pointer select-none hover:text-term-text" onClick={() => handleSort('asset_type')}>Platform{sortIndicator('asset_type')}</th>
                  <th className="cursor-pointer select-none hover:text-term-text" onClick={() => handleSort('domain')}>Domain{sortIndicator('domain')}</th>
                  <th className="cursor-pointer select-none hover:text-term-text text-right" onClick={() => handleSort('assigned_count')}>Assigned{sortIndicator('assigned_count')}</th>
                  <th className="cursor-pointer select-none hover:text-term-text text-right" onClick={() => handleSort('mc_rate')}>MC Rate{sortIndicator('mc_rate')}</th>
                  <th>Goal</th>
                  <th>Trend</th>
                  <th className="cursor-pointer select-none hover:text-term-text text-right" onClick={() => handleSort('readiness_score')}>Readiness{sortIndicator('readiness_score')}</th>
                  <th className="cursor-pointer select-none hover:text-term-text text-right" onClick={() => handleSort('threat_exposure')}>Threat{sortIndicator('threat_exposure')}</th>
                  <th>Fuel</th>
                  <th>Ord</th>
                  <th className="cursor-pointer select-none hover:text-term-text" onClick={() => handleSort('current_tempo')}>Tempo{sortIndicator('current_tempo')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(a => (
                  <tr key={a.id} className="cursor-pointer transition-colors hover:bg-term-bgLight/70" onClick={() => onSelectAsset(a.id)}>
                    <td className="font-bold text-term-text">
                      <div className="flex items-center gap-2">
                        <AssetSilhouette assetType={a.asset_type} width={40} height={16} color={domainColor(a.domain)} className="opacity-60 shrink-0" />
                        <span className="truncate">{a.asset_type}</span>
                      </div>
                    </td>
                    <td>
                      <span className="domain-chip text-[10px]" style={{ backgroundColor: `${domainColor(a.domain)}20`, color: domainColor(a.domain), borderColor: `${domainColor(a.domain)}40` }}>
                        {a.domain}
                      </span>
                    </td>
                    <td className="text-right tabular-nums">{a.assigned_count}</td>
                    <td className="text-right tabular-nums">
                      <MCRateBar value={a.mc_rate} goal={a.mc_rate_goal} />
                    </td>
                    <td className="text-right tabular-nums text-term-textDim text-xs">{formatPercent(a.mc_rate_goal)}</td>
                    <td className="text-xs text-term-textDim">{a.mc_rate_trend}</td>
                    <td className="text-right tabular-nums">{a.readiness_score.toFixed(0)}</td>
                    <td className="text-right tabular-nums">
                      <span className={a.threat_exposure > 0.5 ? 'text-term-error' : a.threat_exposure > 0.3 ? 'text-term-warning' : 'text-term-success'}>
                        {a.threat_exposure.toFixed(2)}
                      </span>
                    </td>
                    <td>
                      {fuelMap[a.id] ? (
                        <CompactFuelBar pct={fuelMap[a.id].fuel_pct} />
                      ) : (
                        <span className="text-term-textDim text-[10px]">--</span>
                      )}
                    </td>
                    <td>
                      {loadoutMap[a.id] ? (
                        <CompactAmmoBar used={loadoutMap[a.id].stations_used} total={loadoutMap[a.id].stations_total} />
                      ) : (
                        <span className="text-term-textDim text-[10px]">--</span>
                      )}
                    </td>
                    <td>
                      <span className={`${tempoClass(a.current_tempo)} px-2 py-0.5 rounded text-[10px] font-medium`}>
                        {a.current_tempo}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-term-textDim px-1">
            Click row or dot to inspect | Sort by clicking column headers
          </div>
        </>
      )}
    </div>
  )
}

// Sub-components
function StatCard({ label, value, icon, highlight }: { label: string; value: string; icon: string; highlight?: boolean }) {
  return (
    <div className={`term-box p-3 flex items-center gap-3 ${highlight ? 'border-term-warning/50 glow-warning' : ''}`}>
      <div className="text-xl text-term-textDim opacity-50">{icon}</div>
      <div>
        <div className="text-[10px] text-term-textDim uppercase tracking-wider">{label}</div>
        <div className="text-lg font-bold text-term-text tabular-nums">{value}</div>
      </div>
    </div>
  )
}

function CompactFuelBar({ pct }: { pct: number }) {
  const filled = Math.round(pct * 10)
  const color = pct >= 0.60 ? '#22c55e' : pct >= 0.30 ? '#f59e0b' : pct >= 0.15 ? '#ef4444' : '#6b7280'
  return (
    <div className="flex items-center gap-1">
      <div className="flex gap-[1px]">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="w-[4px] h-2.5 rounded-[1px]" style={{ backgroundColor: i < filled ? color : '#3a3a5c40' }} />
        ))}
      </div>
      <span className="text-[9px] tabular-nums" style={{ color }}>{Math.round(pct * 100)}%</span>
    </div>
  )
}

function CompactAmmoBar({ used, total }: { used: number; total: number }) {
  const pct = total > 0 ? used / total : 0
  const color = pct > 0.7 ? '#22c55e' : pct > 0.4 ? '#f59e0b' : '#ef4444'
  return (
    <div className="flex items-center gap-1">
      <div className="flex gap-[1px]">
        {Array.from({ length: total }, (_, i) => (
          <div key={i} className="w-[5px] h-2.5 rounded-[1px]" style={{ backgroundColor: i < used ? color : '#3a3a5c40' }} />
        ))}
      </div>
      <span className="text-[9px] tabular-nums text-term-textDim">{used}/{total}</span>
    </div>
  )
}

function MCRateBar({ value, goal }: { value: number; goal: number }) {
  const pct = Math.round(value * 100)
  const isBelowGoal = value < goal
  const isCritical = value < 0.5
  const barColor = isCritical ? '#ef4444' : isBelowGoal ? '#f59e0b' : '#22c55e'
  const filled = Math.round(value * 10)

  return (
    <div className={`flex items-center gap-1.5 ${isCritical ? 'mc-critical' : ''}`}>
      <div className="flex gap-[1px]">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="w-[5px] h-3 rounded-[1px]" style={{ backgroundColor: i < filled ? barColor : '#3a3a5c40' }} />
        ))}
      </div>
      <span className="text-[10px] tabular-nums w-8 text-right" style={{ color: barColor }}>{pct}%</span>
    </div>
  )
}
